import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";

// Predefined delete reasons for different types of operations
const DELETE_REASONS = {
  event: [
    "Event cancelled",
    "Duplicate event",
    "Test event cleanup",
    "Event completed",
    "Data migration",
    "Other",
  ],
  applicant: [
    "Duplicate registration",
    "Invalid information",
    "No longer attending",
    "Requested removal",
    "Data cleanup",
    "Other",
  ],
  stand: [
    "Stand cancelled",
    "Company withdrawal",
    "Duplicate stand",
    "Space reallocation",
    "Other",
  ],
  payment: [
    "Payment method outdated",
    "Configuration error",
    "Security concern",
    "Method no longer supported",
    "Other",
  ],
  link: [
    "Link no longer needed",
    "Security concern",
    "Outdated information",
    "Duplicate link",
    "Other",
  ],
  default: [
    "Data cleanup",
    "Duplicate entry",
    "Invalid data",
    "User request",
    "System maintenance",
    "Other",
  ],
};

export default function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title = "Confirm Deletion",
  itemName,
  itemType = "default",
  loading = false,
  requireReason = true,
  customReasons = null,
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      reason: "",
      customReason: "",
      confirmText: "",
    },
  });

  const selectedReason = watch("reason");
  const reasons =
    customReasons || DELETE_REASONS[itemType] || DELETE_REASONS.default;
  const isCustomReason = selectedReason === "Other";

  const onSubmit = (data) => {
    const deleteReason = isCustomReason ? data.customReason : data.reason;
    onConfirm(deleteReason);
  };

  const handleClose = () => {
    setValue("reason", "");
    setValue("customReason", "");
    setValue("confirmText", "");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" color="error" fontWeight="bold">
          {title}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            <Alert severity="warning">
              <Typography variant="body2">
                This action cannot be undone. The {itemName} will be permanently
                deleted.
              </Typography>
            </Alert>

            {requireReason && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Please provide a reason for deletion:
                </Typography>

                <Controller
                  name="reason"
                  control={control}
                  rules={{ required: "Please select a reason for deletion" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.reason}>
                      <InputLabel>Delete Reason</InputLabel>
                      <Select {...field} label="Delete Reason">
                        {reasons.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.reason && (
                        <Typography variant="caption" color="error">
                          {errors.reason.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />

                {isCustomReason && (
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="customReason"
                      control={control}
                      rules={{
                        required: isCustomReason
                          ? "Please provide a custom reason"
                          : false,
                        minLength: {
                          value: 5,
                          message: "Reason must be at least 5 characters",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Custom Reason"
                          multiline
                          rows={2}
                          error={!!errors.customReason}
                          helperText={errors.customReason?.message}
                          placeholder="Please explain why you're deleting this item..."
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> This deletion will be logged for audit
                purposes.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            color="error"
            variant="contained"
            loading={loading}
            disabled={requireReason && !selectedReason}
          >
            Delete Permanently
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Hook for easy integration
export function useDeleteConfirmation() {
  const [deleteState, setDeleteState] = React.useState({
    open: false,
    item: null,
    itemType: "default",
    loading: false,
  });

  const openDeleteDialog = (item, itemType = "default") => {
    setDeleteState({
      open: true,
      item,
      itemType,
      loading: false,
    });
  };

  const closeDeleteDialog = () => {
    setDeleteState({
      open: false,
      item: null,
      itemType: "default",
      loading: false,
    });
  };

  const setLoading = (loading) => {
    setDeleteState((prev) => ({ ...prev, loading }));
  };

  return {
    deleteState,
    openDeleteDialog,
    closeDeleteDialog,
    setLoading,
  };
}
